import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { combineLatest, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { FeatureCollection, Point } from 'geojson'
import { XyzTileModel } from '../models/xyz-tile.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { ObservationProperties } from '../models/observation.model'
import { ObservationSources } from '../enums/observation-categories.enum'

@Injectable()
export class DataObservationsService {
  private observations: FeatureCollection<Point, ObservationProperties> = {
    type: 'FeatureCollection',
    features: [],
  }
  public isDataInitialized = false

  // Add request cancellation mechanism
  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private observationProvider: ObservationSources,
  ) {}

  async loadObservationData(
    isHistoryEnabled: boolean,
    xyzTiles: XyzTileModel[],
    timestamp?: Date,
    bounds?: maplibregl.LngLatBounds,
  ) {
    // Cancel any previous ongoing request
    this.cancelPreviousRequest$.next()
    this.isDataInitialized = true
    if (isHistoryEnabled && timestamp && bounds) {
      const observations = await this.loadObservationsForHistory(timestamp, bounds)
      this.observations.features = observations.features
    }

    if (!isHistoryEnabled && xyzTiles) {
      const observationsRequests$ = xyzTiles.map(async (tile) => {
        let observations: FeatureCollection<Point, ObservationProperties>

        observations = await this.loadObservations(tile)

        return observations
      })
      const observations$ = combineLatest(observationsRequests$)
      const observationsEvents = await firstValueFrom(observations$)
      this.observations.features = []

      observationsEvents.map((observationsEvent) => {
        if (observationsEvent) {
          this.observations.features = this.observations.features.concat(observationsEvent.features)
        }
      })
    }

    return this
  }

  async loadRawEvents(xyzTiles: XyzTileModel[]) {
    return this.loadObservationData(false, xyzTiles)
  }

  async loadObservations(
    xyzTiles: XyzTileModel,
  ): Promise<FeatureCollection<Point, ObservationProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(
      `${this.config.backend_url}vzo/observations/${xyzTiles.z}/${xyzTiles.x}/${xyzTiles.y}/${this.observationProvider}`,
    )

    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, ObservationProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    return result
  }

  async loadObservationsForHistory(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<Point, ObservationProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }

    const url = new URL(
      `${this.config.backend_url}vzo/observations/historic/${this.observationProvider}`,
    )

    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, ObservationProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )

    return result
  }

  getObservations(): FeatureCollection<Point, ObservationProperties> {
    return this.observations
  }

  // Method to manually cancel current requests (if needed by external services)
  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  // Clean up on service destruction
  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
